import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { User as UserIcon, KeyRound, Eye, EyeOff, Check, Trash2, ShieldAlert } from 'lucide-react';
import { Card, CardTitle, CardDescription, Button, Stat, Input } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { formatNumber } from '@keywordgod/shared';
import { ROLE_COLOR, ROLE_LABEL, STATUS_COLOR, STATUS_LABEL } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useUserPrefs } from '@/lib/userPrefs';

export function Settings() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <UserIcon className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">설정 / 내정보</span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          본인 API 키와 기본 블로그 URL을 저장해 두면 매번 입력하지 않아도 됩니다.
        </p>
      </header>

      <UserPrefsCard />
      <ProfileCard />
    </div>
  );
}

// ─── 본인 API / 블로그 URL (Phase 44) ────────────────────

function UserPrefsCard() {
  const prefs = useUserPrefs();
  const [apiKey, setApiKey] = useState(prefs.geminiApiKey);
  const [blogUrl, setBlogUrl] = useState(prefs.defaultBlogUrl);
  const [showKey, setShowKey] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  function onSave() {
    prefs.setGeminiApiKey(apiKey.trim());
    prefs.setDefaultBlogUrl(blogUrl.trim());
    setSavedTick(true);
    window.setTimeout(() => setSavedTick(false), 1600);
  }

  function onClear() {
    if (!confirm('저장된 API 키와 블로그 URL을 삭제할까요? (이 브라우저에서만 삭제됩니다)')) return;
    prefs.clearAll();
    setApiKey('');
    setBlogUrl('');
  }

  return (
    <Card className="border-gold/30 bg-gradient-to-br from-gold/5 to-transparent">
      <CardTitle className="flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-gold" /> 본인 API · 기본 블로그 URL
      </CardTitle>
      <CardDescription>
        매번 입력하기 귀찮다면 여기서 저장하세요. <strong>이 브라우저에만 저장</strong>되며 서버 / DB / 로그 어디에도
        보내거나 저장하지 않습니다.
      </CardDescription>

      <div className="mt-4 space-y-4">
        <div>
          <label className="block">
            <span className="text-xs text-gray-400">Gemini API Key (블로그 코치 · 1page 분석 · 경쟁 격차 공용)</span>
            <div className="relative mt-1">
              <Input
                className="font-mono pr-9"
                type={showKey ? 'text' : 'password'}
                placeholder="AIzaSy..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gold"
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>
          <p className="mt-1 text-[11px] text-gray-500">
            발급: <a className="text-gold hover:underline" href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">aistudio.google.com/apikey</a>
            {' '}— 무료 quota 일 1,500회.
          </p>
        </div>

        <div>
          <label className="block">
            <span className="text-xs text-gray-400">내 블로그 URL (블로그 코치 · 경쟁 격차 자동 채움)</span>
            <Input
              className="mt-1"
              placeholder="예: blog.naver.com/myblog"
              value={blogUrl}
              onChange={(e) => setBlogUrl(e.target.value)}
              autoComplete="off"
            />
          </label>
        </div>

        <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-3 text-xs text-amber-100/80 flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 shrink-0 text-amber-300 mt-0.5" />
          <div>
            <strong className="text-amber-200">보안 안내</strong> — 키는 이 브라우저 localStorage 에 평문으로 저장됩니다.
            공용/공유 PC에서는 사용 후 <strong>아래 "전체 삭제"</strong> 를 눌러주세요. 다른 사람이 이 브라우저에 접근
            가능하면 키 노출 위험이 있습니다.
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button variant="gold" onClick={onSave}>
            {savedTick ? <><Check className="h-4 w-4" /> 저장됨!</> : '저장'}
          </Button>
          <Button variant="danger" onClick={onClear}>
            <Trash2 className="h-4 w-4" /> 전체 삭제
          </Button>
        </div>
      </div>
    </Card>
  );
}

function ProfileCard() {
  const user = useAuthStore((s) => s.user);
  const reset = useAuthStore((s) => s.reset);
  const logout = useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      reset();
      window.location.href = '/';
    },
  });

  if (!user) return null;

  return (
    <Card>
      <CardTitle>프로필</CardTitle>
      <CardDescription>Google 계정에 연결되어 있습니다</CardDescription>

      <div className="mt-6 flex items-center gap-4">
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.nickname || user.name}
            className="h-16 w-16 rounded-full ring-2 ring-gold/40 object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] text-black text-2xl font-bold flex items-center justify-center">
            {(user.nickname || user.name).slice(0, 1).toUpperCase()}
          </div>
        )}
        <div>
          <div className="text-lg font-bold text-white">
            {user.nickname || <span className="text-gray-400 italic">(닉네임 미입력)</span>}
          </div>
          <div className="text-xs text-gray-500">{user.name}</div>
          <div className="text-sm text-gray-400">{user.email}</div>
          <div className="mt-2 flex items-center gap-2">
            <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-bold', ROLE_COLOR[user.role])}>
              {ROLE_LABEL[user.role]}
            </span>
            <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-bold', STATUS_COLOR[user.status])}>
              {STATUS_LABEL[user.status]}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Stat label="오늘 사용량" value={`${formatNumber(user.dailyUsage)} 회`} />
        <Stat
          label="가입일"
          value={new Date(user.createdAt * 1000).toLocaleDateString('ko-KR')}
        />
        <Stat
          label="승인일"
          value={
            user.approvedAt
              ? new Date(user.approvedAt * 1000).toLocaleDateString('ko-KR')
              : '대기 중'
          }
        />
      </div>

      {user.status === 'pending' && (
        <p className="mt-6 rounded-lg border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
          운영진의 승인을 기다리고 있습니다. 챌린지 운영팀에 문의해주세요.
        </p>
      )}

      <div className="mt-8 flex justify-end">
        <Button variant="danger" onClick={() => logout.mutate()} disabled={logout.isPending}>
          로그아웃
        </Button>
      </div>
    </Card>
  );
}
